import {
  NMT_DAY,
  NMT_OFFICIAL,
  NMT_SUBJECTS,
  type NmtQuestion,
  type NmtSubject,
  daysTo,
  nmtApprox,
  nmtPlan,
  nmtPractice,
  preparesForNmt,
  todayIso,
  useNmt,
  useNmtActions,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { ArrowRightIcon } from '@phosphor-icons/react/ArrowRight';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { Spinner } from '../components/ui';
import {
  Block,
  DARK,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  grad,
  pillClass,
} from '../design/forms';
import { CheckIcon, XIcon } from '../design/icons';

/*
 * «Підготовка до НМТ» in «Форми 2» (28.10): the title on the paper with what it is; on the left the
 * subjects — two more to pick as chips with their pictures, each chosen subject a soft card in its
 * colour with its picture big and quiet behind, the last score, the tries as small bars and a dark pill
 * to a practice test; on the right how many days are left on lavender and the plan of the week as
 * numbered rows. A practice test is one white block: the question light and big, the answers as soft
 * tiles — the right one mint, a wrong one coral — and why under them.
 */

const subjectOf = (key: NmtSubject) => NMT_SUBJECTS.find((s) => s.key === key);
const lookOf = (key: NmtSubject) => {
  const look = subjectLook(subjectOf(key)?.name ?? '');
  return { icon: look.icon, tone: SUBJECT_GRAD[look.tone] };
};

/** One practice test: question by question, the explanation after each, the score at the end. */
function Practice({
  subject,
  onDone,
  save,
}: {
  subject: NmtSubject;
  onDone: () => void;
  save: (a: { correct: number; total: number; missed: string[] }) => void;
}) {
  const { t } = useTranslation();
  const [seed, setSeed] = useState(() => Date.now() % 100_000);
  const [questions, setQuestions] = useState<NmtQuestion[]>(() => nmtPractice(subject, seed));
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<number>();
  const [answers, setAnswers] = useState<boolean[]>([]);
  const q = questions[index];
  const correct = answers.filter(Boolean).length;
  const missed = questions.filter((_, i) => answers[i] === false).map((x) => x.topic);
  const look = lookOf(subject);

  if (!q) {
    const share = questions.length ? correct / questions.length : 0;
    const tone: Grad =
      share >= 0.85 ? 'lime' : share >= 0.6 ? 'sky' : share >= 0.4 ? 'butter' : 'coral';
    return (
      <div className="flex flex-col items-center py-6 text-center">
        <TaskArt kind="done" tone={tone} size={104} />
        <p
          className="mt-4 font-display text-[30px] leading-[38px] font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {t('nmt.result', {
            correct,
            total: questions.length,
            score: nmtApprox(correct, questions.length),
          })}
        </p>
        <p className="mt-1 text-[13px]" style={{ color: INK_FAINT }}>
          {t('nmt.resultHint')}
        </p>
        <p className="mt-4 max-w-xl text-[15px] leading-[22px]" style={{ color: INK }}>
          {missed.length === 0
            ? t('nmt.allRight')
            : t('nmt.missed', { topics: [...new Set(missed)].join(', ') })}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => {
              const next = seed + 1;
              setSeed(next);
              setQuestions(nmtPractice(subject, next));
              setIndex(0);
              setAnswers([]);
            }}
            className={`${pillClass('dark')} pr-4.5`}
          >
            {t('nmt.again')}
          </button>
          <button type="button" onClick={onDone} className={`${pillClass('light')} pr-4.5`}>
            {t('nmt.close')}
          </button>
        </div>
      </div>
    );
  }

  const answered = picked !== undefined;
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <SubjectArt icon={look.icon} tone={look.tone} size={40} />
        <p className="min-w-0 flex-1 text-[13.5px] leading-5" style={{ color: INK_SOFT }}>
          <span className="block text-[15px] font-medium" style={{ color: INK }}>
            {subjectOf(subject)?.name}
          </span>
          {t('nmt.question', { n: index + 1, total: questions.length })} · {q.topic}
        </p>
        <button type="button" onClick={onDone} className={`${pillClass('light', 'sm')} pr-3.5`}>
          {t('nmt.close')}
        </button>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: PAPER }}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${(100 * (index + (answered ? 1 : 0))) / questions.length}%`,
            backgroundColor: DARK,
          }}
        />
      </div>
      <p
        className="max-w-3xl font-display text-[26px] leading-[33px] font-light tracking-[-0.025em]"
        style={{ color: INK }}
      >
        {q.text}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {q.options.map((option, i) => {
          const right = answered && i === q.correct;
          const wrong = answered && i === picked && i !== q.correct;
          const tone: Grad | undefined = right ? 'mint' : wrong ? 'coral' : undefined;
          return (
            <button
              key={option}
              type="button"
              disabled={answered}
              onClick={() => {
                setPicked(i);
                setAnswers([...answers, i === q.correct]);
              }}
              className={`flex items-center gap-3 rounded-[18px] p-3 pr-4 text-left text-[15px] leading-[21px] transition-colors ${answered ? '' : 'hover:bg-[#EFECE5]'} ${answered && !tone ? 'opacity-55' : ''}`}
              style={
                tone
                  ? { backgroundImage: grad(tone), color: INK }
                  : { backgroundColor: PAPER, color: INK }
              }
            >
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white font-display text-[17px] font-light"
                style={{ color: tone ? GRAD[tone].ink : INK }}
              >
                {right ? (
                  <CheckIcon size={15} weight="bold" aria-hidden />
                ) : wrong ? (
                  <XIcon size={15} weight="bold" aria-hidden />
                ) : (
                  'АБВГ'[i]
                )}
              </span>
              <span className={wrong ? 'line-through' : ''}>{option}</span>
            </button>
          );
        })}
      </div>
      {answered && (
        <div className="motion-rise flex flex-wrap items-center justify-between gap-3">
          <p
            className="max-w-2xl flex-1 basis-72 rounded-[16px] p-3.5 text-[14.5px] leading-[21px]"
            style={{ backgroundColor: PAPER, color: INK_SOFT }}
          >
            {q.explain}
          </p>
          <button
            type="button"
            onClick={() => {
              const last = index + 1 >= questions.length;
              if (last) save({ correct, total: questions.length, missed });
              setIndex(index + 1);
              setPicked(undefined);
            }}
            className={pillClass('dark')}
          >
            {index + 1 < questions.length ? t('nmt.next') : t('nmt.finish')}
            <PillIcon icon={ArrowRightIcon} />
          </button>
        </div>
      )}
    </div>
  );
}

/** How long still: the days big on lavender, the plane of «скоро» quiet behind. */
function Countdown({ days, className = '' }: { days: number; className?: string }) {
  const { t } = useTranslation();
  return (
    <section
      className={`relative isolate overflow-hidden rounded-[28px] p-5 md:p-6 ${className}`}
      style={{ backgroundImage: grad('lavender'), color: GRAD.lavender.ink }}
    >
      <TaskArt
        kind="soon"
        tone="lavender"
        size={150}
        ghost
        className="pointer-events-none absolute -top-4 -right-6 -z-10"
      />
      <p
        className="font-display text-[30px] leading-[36px] font-light tracking-[-0.035em]"
        style={{ color: INK }}
      >
        {t('nmt.daysLeft', { count: days })}
      </p>
      <p className="mt-2 text-[13px]">{t('nmt.approx')}</p>
    </section>
  );
}

/**
 * «Підготовка до НМТ»: the countdown, the subjects (two required, two to choose), practice tests
 * and the plan of the week from the topics that went wrong. The family sees it, the school does not.
 */
export function NmtPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const self = ctx.kind === 'student';
  const data = useNmt(child?.memberId ?? '');
  const actions = useNmtActions(ctx.schoolId, child?.memberId ?? '');
  const [running, setRunning] = useState<NmtSubject>();
  const today = todayIso();
  if (!child || !preparesForNmt(child.class?.grade)) {
    return (
      <div className="flex flex-col gap-4">
        <PageHead title={t('nmt.title')} />
        <Block>
          <div className="flex flex-col items-center py-12 text-center">
            <TaskArt kind="soon" tone="lavender" size={96} />
            <p className="mt-3 max-w-md text-[15px]" style={{ color: INK_SOFT }}>
              {t('nmt.notYet')}
            </p>
          </div>
        </Block>
      </div>
    );
  }
  const electives = data.data?.electives ?? [];
  const attempts = data.data?.attempts ?? [];
  const mine: NmtSubject[] = ['ukr', 'math', ...electives];
  const plan = nmtPlan(attempts, mine, today);
  const days = daysTo(today, NMT_DAY);
  const toggle = (key: NmtSubject) => {
    const next = electives.includes(key)
      ? electives.filter((x) => x !== key)
      : electives.length < 2
        ? [...electives, key]
        : [electives[1] as NmtSubject, key];
    actions.choose.mutate(next);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('nmt.title')}
        sub={
          <span className="block max-w-2xl text-[15px] leading-[22px]">
            {self ? t('nmt.intro') : t('nmt.introFamily')}
          </span>
        }
      />

      {/* on a phone how long still comes first, before the subjects */}
      {!running && <Countdown days={days} className="lg:hidden" />}
      {running ? (
        <Block>
          <Practice
            key={running}
            subject={running}
            onDone={() => setRunning(undefined)}
            save={(a) => actions.save.mutate({ subject: running, ...a })}
          />
        </Block>
      ) : data.isPending ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <Block>
            <Head title={t('nmt.subjects')} count={mine.length} />
            {self && (
              <div className="mb-5">
                <p className="mb-2 text-[13px] font-medium" style={{ color: INK_SOFT }}>
                  {t('nmt.pickTwo')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {NMT_SUBJECTS.filter((s) => !s.required).map((s) => {
                    const on = electives.includes(s.key);
                    const look = lookOf(s.key);
                    return (
                      <button
                        key={s.key}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggle(s.key)}
                        className={`inline-flex h-10 items-center gap-2 rounded-full pr-3.5 pl-1.5 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`}
                        style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
                      >
                        <SubjectArt icon={look.icon} tone={look.tone} size={28} />
                        {s.name}
                        {on && <CheckIcon size={13} weight="bold" aria-hidden />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {mine.map((key) => {
                const s = subjectOf(key);
                const look = lookOf(key);
                const tries = attempts.filter((a) => a.subject === key);
                const last = tries[0];
                return (
                  <li
                    key={key}
                    className="relative isolate flex min-h-[200px] min-w-0 flex-col gap-1 overflow-hidden rounded-[24px] p-4 md:p-5"
                    style={{ backgroundImage: grad(look.tone), color: GRAD[look.tone].ink }}
                  >
                    <SubjectArt
                      icon={look.icon}
                      tone={look.tone}
                      size={140}
                      ghost
                      className="pointer-events-none absolute -top-5 -right-5 -z-10"
                    />
                    {s?.required && (
                      <span
                        className="self-start rounded-[7px] bg-white/80 px-2 py-1 text-[12px] leading-none font-medium"
                        style={{ color: INK }}
                      >
                        {t('nmt.required')}
                      </span>
                    )}
                    <p
                      className="mt-1 max-w-[80%] font-display text-[24px] leading-[30px] font-light tracking-[-0.025em]"
                      style={{ color: INK }}
                    >
                      {s?.name}
                    </p>
                    <p className="text-[13px]" style={{ color: INK_SOFT }}>
                      {last
                        ? `${t('nmt.last', { score: nmtApprox(last.correct, last.total) })} · ${t('nmt.attempts', { count: tries.length })}`
                        : t('nmt.never')}
                    </p>
                    {tries.length > 1 && (
                      <div className="mt-2 flex h-8 items-end gap-1" aria-hidden>
                        {[...tries]
                          .reverse()
                          .slice(-12)
                          .map((a) => (
                            <span
                              key={a.takenAt}
                              className="w-2.5 rounded-[3px]"
                              style={{
                                height: `${Math.max(12, (100 * a.correct) / a.total)}%`,
                                backgroundColor: GRAD[look.tone].ink,
                                opacity: 0.75,
                              }}
                            />
                          ))}
                      </div>
                    )}
                    {self && (
                      <button
                        type="button"
                        onClick={() => setRunning(key)}
                        className={`${pillClass('dark')} mt-auto self-start`}
                      >
                        {t('nmt.start')}
                        <PillIcon icon={ArrowRightIcon} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className="mt-4 text-[12.5px]" style={{ color: INK_FAINT }}>
              {t('nmt.official')}:{' '}
              <a href={NMT_OFFICIAL} target="_blank" rel="noreferrer" className="underline">
                testportal.gov.ua
              </a>
            </p>
          </Block>

          <div className="flex min-w-0 flex-col gap-4">
            <Countdown days={days} className="hidden lg:block" />

            <Block>
              <Head title={self ? t('nmt.plan') : t('nmt.planFamily')} />
              {plan.topics.length === 0 ? (
                <p className="text-[15px] leading-[22px]" style={{ color: INK_SOFT }}>
                  {t('nmt.planEmpty')}
                </p>
              ) : (
                <>
                  <p className="mb-2 text-[13px] font-medium" style={{ color: INK_SOFT }}>
                    {t('nmt.week')}
                  </p>
                  <ol className="flex flex-col gap-1.5">
                    {plan.thisWeek.map((x, i) => {
                      const look = lookOf(x.subject);
                      return (
                        <li
                          key={`${x.subject}-${x.topic}`}
                          className="flex items-center gap-3 rounded-[16px] p-2.5 pr-3"
                          style={{ backgroundColor: PAPER }}
                        >
                          <span
                            className="w-6 shrink-0 text-center font-display text-[24px] leading-none font-light"
                            style={{ color: INK_FAINT }}
                          >
                            {i + 1}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span
                              className="block text-[15px] leading-5 font-medium"
                              style={{ color: INK }}
                            >
                              {x.topic}
                            </span>
                            <span className="block text-[12.5px]" style={{ color: INK_SOFT }}>
                              {subjectOf(x.subject)?.name}
                            </span>
                          </span>
                          <SubjectArt icon={look.icon} tone={look.tone} size={32} />
                        </li>
                      );
                    })}
                  </ol>
                  {plan.topics.length > 3 && (
                    <p className="mt-3 text-[13.5px] leading-5" style={{ color: INK_SOFT }}>
                      {t('nmt.later', {
                        topics: plan.topics
                          .slice(3, 8)
                          .map((x) => x.topic)
                          .join(', '),
                      })}
                    </p>
                  )}
                  <p className="mt-2 text-[12.5px]" style={{ color: INK_FAINT }}>
                    {t('nmt.weeks', { count: plan.weeksLeft })}
                  </p>
                </>
              )}
            </Block>
          </div>
        </div>
      )}
    </div>
  );
}
